import subDays from "date-fns/subDays";
import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCPopover, RCCServicesApi, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import downloadFile from "src/utils/downloadFile";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import OverflowSpan, { StyledDivDescriptionAndAlertExclamation } from "../styles";

const ListInstalledServices: React.FC = () => {

	const { translate, currentLanguage } = useTranslation();
	const { superUser } = useAuth();
	const {addToast} = RCCHookToast();
	
	const paginationRef = useRef<IForwardPagination>(null);


	const [showFilters, setShowFilters] = useState(false);
	const [loadingDowloadService, setLoadingDowloadService] = useState(false);
	const [filters, setFilters] = useState<{ filter: string, group: string, endDate: Date | null, initDate: Date | null}>({ group: "", filter: "", endDate: new Date(), initDate: subDays(new Date(),30) });
	const { handleSubmit : handleSubmitFilters, setValue: setValueFilters, control: controlFilters, register : registerFilters, reset: resetFilters, watch: watchFilters } = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as any;
			newState.filter =  data.filter;
			newState.endDate = data.endDate;
			newState.initDate = data.initDate;
			if(data.group)
				newState.group = typeof data.group === "object" ? data.group?.value : data.group;
			return newState;
		});
		paginationRef.current?.reload();
	};

	const colIp = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<OverflowSpan title={row.efa015Apikey}>
				{row.efa015Apikey}
			</OverflowSpan>
			<StyledDivDescriptionAndAlertExclamation>
				<RCCPopover
					color="grey"
					header={{
					  title: translate("Ip")
					}}
					body={{
						custom: <>
							<p>{`${translate("Ip Interno")}: ${row.efa015Ipinterno}`}</p>
							<p>{`${translate("Ip Externo")}: ${row.efa015Ipexterno}`}</p>
						</>
					}}
					position="right"
				>
					<div className="infoCircleDescription-kr">
						<RCCIcon name="exclamationCircle" color="var(--grey)" size={16} />
					</div>
				</RCCPopover>
			</StyledDivDescriptionAndAlertExclamation>
		</RCCGrid>
	);

	const colStation = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<OverflowSpan title={row.efa015Estacao}>
				{row.efa015Estacao}
			</OverflowSpan>
			<StyledDivDescriptionAndAlertExclamation>
				<RCCPopover
					color="grey"
					header={{
					  title: translate("Executável")
					}}
					body={{
						text: row.efa015Executavel
					}}  
					position="right"
				>
					<div className="infoCircleDescription-kr">
						<RCCIcon name="exclamationCircle" color="var(--grey)" size={16} />
					</div>
				</RCCPopover>
			</StyledDivDescriptionAndAlertExclamation>
		</RCCGrid>
	);

	const colSituation = (row: any) => (
		<RCCGrid
			container
			item
			style={{ justifyContent: "flex-end !important"}}
		>
			{formatDate(row.efa015Dtultverificacao, currentLanguage, true)}
		</RCCGrid>
	);

	const dowloadService = async () => {
		setLoadingDowloadService(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				responseType: "blob",
				contentType: "application/x-www-form-urlencoded",
				method: "Attachments/DownloadService"
			});

			downloadFile(data, "service_scoreplan.zip");

			setLoadingDowloadService(false);
		}catch(err: any){
			setLoadingDowloadService(false);
			standardErrorMessages(err, translate("Erro ao buscar serviço"), addToast);
		}
	};


	return (
		<>
    	<RCCGrid
				container
				item
				style={{ marginTop: "3rem" }}
			>
				<PageTitle
					title={translate("Serviços Instalados")}
					icon={{
						name: "tools"
					}}
				>
					<RCCTooltip
						text={translate("Baixar Serviço")} 
					  color="grey"
					>
						<RCCButton
							icon={{name: "download", color: "#FFF", size: 16}}
							color="grey"
							layout="isRounded"
							size="md"
							loading={loadingDowloadService}
						  onClick={dowloadService}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")} 
					  color={showFilters || filters.group || filters.initDate || filters.endDate || filters.filter  ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.group || filters.initDate || filters.endDate || filters.filter  ? "blue" : "grey"}
							layout="isRounded"
							size="md"
						  onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
			</RCCGrid>

    	{/* Filters */}
			{showFilters && (
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters(
						{ group: "", filter: "", endDate: null, initDate: null})}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
							xs={10}
						>
							{superUser ? (
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={4}
									lg={4}
								>
									<LovBusinessGroups 
										control={controlFilters}
										name="group" 
										defaultValue={filters.group ?? ""}
										label={translate("Grupo de Empresas")}
										submit="value and label"
									/>
								</RCCGrid>
							) : null}
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<DateTimePickerRange 
									type="datePicker"
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit="initDate"
									nameEnd="endDate"
									setValue={setValueFilters}
									control={controlFilters}
									defaultValueEnd={filters.endDate}
									defaultValueInit={filters.initDate}	
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
			)}

			<Pagination 
				ref={paginationRef}
				dataOrRoute="Integrations/ListInstalledServices" 
				filtersData={{
					...filters,
					initDate: filters.initDate ? filters.initDate.toLocaleDateString() : "",
					endDate: filters.endDate ? filters.endDate.toLocaleDateString() : ""
				}} 				
				columns={[
					{
						idColumn: "efa015Apikey",
						nameColumn: "Api Key",
						xs: 12,
						sm: 4,
						justify: "left",
						type: "string",
						truncate: true,
						cellRenderer: colIp
					},
					{
						idColumn: "efa015Estacao",
						nameColumn: translate("Estação"),
						xs: 12,
						sm: 3,
						justify: "left",
						type: "string",
						truncate: true,
						cellRenderer: colStation
					},
					{
						idColumn: "efa015Usuarioso",
						nameColumn: translate("Usuário"),
						xs: 12,
						sm: 2,
						justify: "left",
						type: "string",
						truncate: true
					},
					{
						idColumn: "efa015Versao",
						nameColumn: translate("Versão"),
						xs: 12,
						sm: 1,
						justify: "left",
						type: "string",
						truncate: true
					},
					{
						idColumn: "efa015Dtultverificacao",
						nameColumn: translate("Última verificação"),
						xs: 12,
						sm: 2,
						justify: "left",
						type: "string",
						cellRenderer: colSituation,
						truncate: true
					}
				]}			
			/>
	
		</>
	);
};

export default ListInstalledServices;
